import { img } from "../images";
import type { Service, ServiceCategory } from "./types";

export const andrologyCategory: ServiceCategory = {
  slug: "andrology",
  name: "Andrology",
  short: "Men's reproductive and sexual health, in confidence.",
  tagline: "Men's health, in a private and professional setting.",
  image: img.andrology,
  doctorSlug: "dr-m-v-arun-vijaiyan",
  intro:
    "Andrology at AV Clinic focuses on men's reproductive and sexual health, including male infertility, reproductive health and men's sexual health concerns, in a private, professional and judgement-free setting.",
  description:
    "Andrology is the branch of medicine devoted to men's health — covering reproductive health, fertility and sexual well-being. These are sensitive topics that many men hesitate to discuss, yet they can have a significant impact on quality of life, relationships and confidence. AV Clinic provides a private, professional and judgement-free space where these concerns can be raised openly. Dr. M. V. Arun Vijaiyan takes time to understand the full picture — history, lifestyle and expectations — before recommending any investigation or treatment. The approach is always respectful, clear and confidential, with the goal of helping every man understand his health and the practical options available.",
  conditions: [
    "male-infertility",
    "male-reproductive-health",
    "male-sexual-health",
    "mens-health",
    "andrology-consultation",
  ],
  concerns: [
    "Male infertility",
    "Male reproductive health",
    "Male sexual health concerns",
    "Men's wellness and health checks",
  ],
  management: [
    "Confidential andrology consultation",
    "Evaluation of male infertility and reproductive health",
    "Assessment of men's sexual health concerns",
    "Clear explanation of findings and options",
    "Appropriate referral and follow-up care",
  ],
  faqs: [
    {
      q: "Is an andrology consultation private?",
      a: "Yes. Consultations are conducted in a private and professional environment. All discussions and information are handled with strict confidentiality.",
    },
    {
      q: "Should I feel embarrassed to discuss these concerns?",
      a: "No. These are common and important health topics. Our consultations are professional, respectful and judgement-free.",
    },
    {
      q: "Do I need a referral to book?",
      a: "No, you can book an andrology consultation directly. Your privacy is fully respected throughout.",
    },
  ],
  related: ["male-infertility", "male-sexual-health", "male-reproductive-health", "mens-health"],
  diagnosisSteps: [
    { title: "Private Consultation", desc: "Your concerns are discussed confidentially." },
    { title: "History & Lifestyle Review", desc: "Relevant history and lifestyle factors are considered." },
    { title: "Assessment & Tests When Needed", desc: "Investigations are used selectively and explained clearly." },
    { title: "Clear, Respectful Plan", desc: "Findings and next steps are discussed openly." },
  ],
  specialistApproach: [
    { icon: "ri-headphone-line", title: "Listen", desc: "Understand your concerns in a confidential setting." },
    { icon: "ri-search-eye-line", title: "Assess", desc: "Review history and perform a focused assessment." },
    { icon: "ri-stethoscope-line", title: "Diagnose", desc: "Identify the cause with appropriate evaluation." },
    { icon: "ri-calendar-check-line", title: "Plan", desc: "Agree on practical, respectful next steps." },
  ],
  patientEducation: [
    { icon: "ri-list-check-2", title: "Before Your Visit", desc: "Think about your concerns, how long they have been present and what you'd like to understand." },
    { icon: "ri-file-list-3-line", title: "Bring Along", desc: "Any previous reports or prescriptions. Privacy is fully respected." },
    { icon: "ri-question-line", title: "Questions to Ask", desc: "Ask about causes, tests, treatment options and realistic expectations." },
    { icon: "ri-refresh-line", title: "Plan Follow-Up", desc: "Many concerns benefit from a clear follow-up plan." },
  ],
  myths: [
    { myth: "Men's health concerns are rare.", fact: "Men's reproductive and sexual health concerns are common, and many can be effectively evaluated and managed." },
    { myth: "Discussing these issues is embarrassing.", fact: "They are important health topics. Consultations are professional, respectful and confidential." },
  ],
  seo: {
    title: "Andrologist in Krishnagiri | Men's Health | AV Clinic",
    description:
      "Specialist andrology in Krishnagiri — male infertility, reproductive and sexual health in a private, professional setting by Dr. M. V. Arun Vijaiyan at AV Clinic. Book a consultation.",
  },
};

export const andrologyServices: Service[] = [
  {
    slug: "male-infertility",
    name: "Male Infertility",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "Compassionate evaluation and guidance for male infertility.",
    tagline: "Understanding Male Infertility, Together.",
    intro:
      "Male infertility can affect a couple's ability to conceive, and male factors are involved more often than many realise. AV Clinic offers confidential evaluation and clear guidance to help men understand and address fertility concerns.",
    image: img.menHealth,
    symptoms: [
      { name: "Difficulty conceiving", desc: "A couple unable to conceive after trying for a reasonable period." },
      { name: "Semen analysis concerns", desc: "Findings from a semen test that need explanation." },
      { name: "Reproductive health questions", desc: "Concerns about factors that may affect fertility." },
    ],
    causes: [
      "Low or absent sperm count",
      "Sperm movement or shape problems",
      "Hormonal factors",
      "Varicocele (enlarged veins in the scrotum)",
      "Lifestyle factors (smoking, alcohol, weight)",
      "Infections or other medical conditions",
    ],
    riskFactors: [
      "Varicocele",
      "Smoking or excess alcohol",
      "Obesity",
      "Certain medications or treatments",
      "History of infections or surgery",
    ],
    whenToSee: [
      "A couple has been trying to conceive without success",
      "A semen analysis needs interpretation",
      "You have known risk factors for infertility",
    ],
    diagnosisSteps: [
      { title: "Confidential Consultation", desc: "Your history and concerns are discussed privately." },
      { title: "Semen & Hormone Assessment", desc: "Simple tests such as a semen analysis are used where appropriate." },
      { title: "Clinical Evaluation", desc: "A focused physical assessment when needed." },
      { title: "Clear Explanation", desc: "Findings and options are explained with respect." },
    ],
    treatment: {
      medical: [
        "Treatment of reversible causes where possible",
        "Guidance on optimising fertility",
        "Referral for coordinated care where appropriate",
      ],
      procedures: [],
      lifestyle: [
        "Quitting smoking and limiting alcohol",
        "Maintaining a healthy weight",
        "Reducing stress",
      ],
      followUp:
        "Fertility assessment often involves coordinated care. Follow-up is individualised and respectful.",
    },
    myths: [
      { myth: "Infertility is rarely a male problem.", fact: "Male factors contribute to infertility in a significant proportion of couples, and many can be identified and addressed." },
      { myth: "Nothing can be done for male infertility.", fact: "Many causes can be evaluated and managed, and couples can be guided toward the right options." },
    ],
    prevention: [
      "Maintain a healthy lifestyle",
      "Avoid smoking and excess alcohol",
      "Seek advice early if conception is difficult",
    ],
    management: [
      "Confidential infertility consultation",
      "Assessment of reproductive health",
      "Clear explanation of findings and options",
      "Referral and coordinated care where needed",
    ],
    faqs: [
      { q: "Is male infertility common?", a: "Yes. Male factors contribute to infertility in a significant proportion of couples, and many issues can be identified and addressed." },
      { q: "What tests are involved?", a: "A semen analysis is the most common first test, with hormone and other assessments used as appropriate." },
      { q: "When should a couple seek help?", a: "Generally after a year of trying without conceiving, or earlier if there are known concerns." },
      { q: "Can lifestyle changes help fertility?", a: "Yes, quitting smoking, limiting alcohol, a healthy weight and reduced stress can all support fertility." },
      { q: "Is the consultation confidential?", a: "Completely. All discussions and information are handled with strict confidentiality." },
      { q: "How can I book a consultation?", a: "You can book through the appointment page, call the clinic, or message us on WhatsApp." },
    ],
    related: ["andrology-consultation", "male-reproductive-health", "mens-health"],
    relatedBlogs: ["male-infertility-questions"],
    seo: {
      title: "Male Infertility Treatment in Krishnagiri | Andrologist | AV Clinic",
      description:
        "Male infertility evaluation in Krishnagiri — confidential, compassionate assessment by an andrologist at AV Clinic. Understand your options and book a consultation.",
    },
  },
  {
    slug: "male-sexual-health",
    name: "Male Sexual Health",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "Private, professional care for men's sexual health.",
    tagline: "Confidential, Respectful Care.",
    intro:
      "Men's sexual health concerns are common and often treatable. AV Clinic provides confidential, judgement-free consultation to help men address these issues and understand the options available.",
    image: img.menHealth,
    symptoms: [
      { name: "Erectile concerns", desc: "Difficulty achieving or maintaining an erection." },
      { name: "Reduced libido", desc: "A lower than usual interest in sex." },
      { name: "Premature ejaculation", desc: "Ejaculation that happens earlier than desired." },
    ],
    causes: [
      "Psychological factors and stress",
      "Hormonal changes",
      "Cardiovascular and metabolic conditions",
      "Medications",
      "Lifestyle factors",
    ],
    riskFactors: [
      "Diabetes and heart disease",
      "Smoking and excess alcohol",
      "Obesity",
      "Stress and anxiety",
      "Certain medications",
    ],
    whenToSee: [
      "Concerns are persistent or affecting relationships",
      "Symptoms affect confidence or quality of life",
      "You want a professional, confidential opinion",
    ],
    diagnosisSteps: [
      { title: "Private Consultation", desc: "Your concern is discussed confidentially and respectfully." },
      { title: "Health & Lifestyle Review", desc: "Medical and lifestyle factors are considered." },
      { title: "Assessment When Needed", desc: "Simple tests may be used to identify contributing causes." },
      { title: "Discussion of Options", desc: "Treatment and lifestyle options are explained." },
    ],
    treatment: {
      medical: [
        "Management of contributing medical conditions",
        "Appropriate treatment where indicated",
        "Referral where coordinated care is needed",
      ],
      procedures: [],
      lifestyle: [
        "Regular exercise",
        "Quitting smoking and limiting alcohol",
        "Managing stress and sleep",
      ],
      followUp:
        "Follow-up depends on the cause and response, and is always conducted with discretion.",
    },
    myths: [
      { myth: "Sexual health concerns are rare.", fact: "They are very common, and most can be evaluated and managed effectively." },
      { myth: "These problems can't be treated.", fact: "Many causes can be identified and treated, or managed with the right support." },
    ],
    prevention: [
      "Maintain cardiovascular health",
      "Manage stress",
      "Limit alcohol and quit smoking",
    ],
    management: [
      "Confidential, private consultation",
      "Identification of contributing factors",
      "Appropriate treatment and lifestyle advice",
    ],
    faqs: [
      { q: "Is it normal to feel embarrassed discussing sexual health?", a: "It is completely understandable. Consultations are private and conducted with professionalism and respect." },
      { q: "Can stress affect sexual health?", a: "Yes, stress and anxiety are common contributing factors. They are addressed as part of a complete assessment." },
      { q: "Are these concerns treatable?", a: "Many are. A proper evaluation identifies contributing causes and guides appropriate management." },
    ],
    related: ["andrology-consultation", "mens-health", "male-reproductive-health"],
    relatedBlogs: ["mens-health-check-what-to-expect"],
    seo: {
      title: "Male Sexual Health in Krishnagiri | Andrologist | AV Clinic",
      description:
        "Private, professional care for men's sexual health in Krishnagiri — confidential consultation with an andrologist at AV Clinic. Book now.",
    },
  },
  {
    slug: "male-reproductive-health",
    name: "Male Reproductive Health",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "Comprehensive care for men's reproductive health.",
    tagline: "Comprehensive Reproductive Health for Men.",
    intro:
      "Male reproductive health includes fertility, hormonal health and overall reproductive well-being. AV Clinic offers assessment and guidance in a confidential setting, helping men stay informed about their health.",
    image: img.menHealth,
    symptoms: [
      { name: "Fertility concerns", desc: "Questions about the ability to father a child." },
      { name: "Hormonal symptoms", desc: "Changes in energy, libido or mood." },
      { name: "Reproductive health checks", desc: "A wish to understand reproductive health." },
    ],
    causes: [
      "Hormonal imbalances",
      "Varicocele and testicular conditions",
      "Infections",
      "Lifestyle factors",
      "Genetic or medical conditions",
    ],
    whenToSee: [
      "You have fertility concerns",
      "You have symptoms that may relate to hormones",
      "You want a reproductive health assessment",
    ],
    diagnosisSteps: [
      { title: "Confidential Assessment", desc: "Your concerns and history are discussed privately." },
      { title: "Focused Evaluation", desc: "Clinical assessment and selective tests." },
      { title: "Clear Explanation", desc: "Findings are explained in plain, respectful language." },
      { title: "Guidance", desc: "Practical next steps and follow-up are agreed." },
    ],
    treatment: {
      medical: [
        "Management of identified conditions",
        "Hormonal evaluation where appropriate",
      ],
      procedures: [],
      lifestyle: [
        "Healthy weight and diet",
        "Quitting smoking",
        "Regular exercise",
      ],
      followUp:
        "Follow-up depends on the findings and any treatment. A clear plan is agreed with you.",
    },
    myths: [
      { myth: "Reproductive health only matters when planning a family.", fact: "Reproductive health is relevant to overall male well-being throughout life." },
    ],
    prevention: [
      "Maintain a healthy lifestyle",
      "Avoid smoking and excess alcohol",
      "Have concerns assessed early",
    ],
    management: [
      "Confidential reproductive health assessment",
      "Investigation where appropriate",
      "Clear guidance and follow-up",
    ],
    faqs: [
      { q: "Can lifestyle affect reproductive health?", a: "Yes. Diet, weight, exercise, stress and habits such as smoking can all influence male reproductive health." },
      { q: "What does a reproductive health check involve?", a: "A confidential discussion, focused assessment and selective tests as needed, with clear explanation." },
    ],
    related: ["andrology-consultation", "male-infertility", "mens-health"],
    relatedBlogs: ["male-infertility-questions"],
    seo: {
      title: "Male Reproductive Health in Krishnagiri | Andrologist | AV Clinic",
      description:
        "Male reproductive health in Krishnagiri — confidential assessment of fertility and hormonal health by an andrologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "mens-health",
    name: "Men's Health",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "Focused attention on men's overall health and well-being.",
    tagline: "Proactive Care for Men's Health.",
    intro:
      "Men's health goes beyond specific complaints to include preventive care and general well-being. AV Clinic helps men stay informed and proactive about their health.",
    image: img.menHealth,
    symptoms: [
      { name: "Preventive health", desc: "A wish to understand and maintain health." },
      { name: "Lifestyle-related concerns", desc: "Concerns linked to diet, weight and habits." },
      { name: "General wellness", desc: "A proactive approach to overall well-being." },
    ],
    causes: [
      "Lifestyle factors",
      "Ageing",
      "Family history",
      "Preventable risk factors",
    ],
    whenToSee: [
      "You want a preventive health assessment",
      "You have questions about lifestyle and risk",
      "You want guidance on appropriate health checks",
    ],
    diagnosisSteps: [
      { title: "Health Review", desc: "Your health, lifestyle and concerns are discussed." },
      { title: "Risk Assessment", desc: "Age and lifestyle risk factors are reviewed." },
      { title: "Guidance", desc: "Appropriate health checks are advised." },
      { title: "Follow-Up Plan", desc: "A preventive plan is agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [],
      lifestyle: [
        "Balanced diet and regular exercise",
        "Quitting smoking and limiting alcohol",
        "Managing stress and sleep",
      ],
      followUp:
        "A follow-up schedule for health checks is agreed based on your age and risk factors.",
    },
    myths: [
      { myth: "Men don't need regular health checks.", fact: "Preventive health checks help detect concerns early, when they are often easier to manage." },
    ],
    prevention: [
      "Stay active and eat well",
      "Limit alcohol and quit smoking",
      "Know your family history",
    ],
    management: [
      "Men's health assessment",
      "Preventive health advice",
      "Guidance on lifestyle and screening",
    ],
    faqs: [
      { q: "How often should men have a health check?", a: "This depends on age and risk factors. A men's health consultation can help you decide an appropriate schedule." },
      { q: "What should a men's health check include?", a: "A review of your health, lifestyle and risk factors, with advice on appropriate checks for your age." },
    ],
    related: ["andrology-consultation", "male-reproductive-health"],
    relatedBlogs: ["mens-health-check-what-to-expect"],
    seo: {
      title: "Men's Health Check in Krishnagiri | Andrologist | AV Clinic",
      description:
        "Men's health check in Krishnagiri — preventive assessment and lifestyle guidance by an andrologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "andrology-consultation",
    name: "Andrology Consultation",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "A confidential first step towards men's health care.",
    tagline: "A Confidential First Step.",
    intro:
      "An andrology consultation is a private, professional space to discuss men's reproductive and sexual health concerns and plan appropriate care — the right first step, whatever your concern.",
    image: img.menHealth,
    symptoms: [
      { name: "Any men's health concern", desc: "Fertility, sexual health or reproductive questions." },
      { name: "Fertility questions", desc: "Concerns about conceiving." },
      { name: "Sexual health concerns", desc: "Issues affecting confidence or relationships." },
    ],
    whenToSee: [
      "You have any men's health concern",
      "You want a specialist, confidential opinion",
      "You need clear guidance on next steps",
    ],
    diagnosisSteps: [
      { title: "Private Discussion", desc: "Your concerns are discussed confidentially." },
      { title: "Focused Assessment", desc: "History and selective evaluation." },
      { title: "Clear Explanation", desc: "Findings are explained respectfully." },
      { title: "Practical Next Steps", desc: "A clear plan is agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [],
      lifestyle: [
        "Guidance tailored to your situation",
      ],
      followUp:
        "Depending on your concern, a follow-up plan is agreed to monitor progress or plan care.",
    },
    management: [
      "Private, confidential consultation",
      "Thorough assessment and discussion",
      "Clear, practical next steps",
    ],
    faqs: [
      { q: "What happens in an andrology consultation?", a: "You will have a private discussion about your concerns, followed by any appropriate assessment and a clear plan." },
      { q: "Is my information kept confidential?", a: "Yes. All consultations and information are handled with strict confidentiality." },
    ],
    related: ["male-infertility", "male-sexual-health", "mens-health"],
    relatedBlogs: ["male-infertility-questions"],
    seo: {
      title: "Andrology Consultation in Krishnagiri | AV Clinic",
      description:
        "Confidential andrology consultation in Krishnagiri — discuss men's reproductive and sexual health with a specialist at AV Clinic. Book now.",
    },
  },
  {
    slug: "erectile-dysfunction",
    name: "Erectile Dysfunction Treatment",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "Confidential evaluation and care for erectile concerns.",
    tagline: "Confidential, Respectful Care.",
    intro:
      "Erectile dysfunction is a common concern that can relate to physical, psychological or lifestyle factors. AV Clinic provides a confidential, judgement-free consultation to identify the causes and plan appropriate care.",
    image: img.svcErectileDysfunction,
    symptoms: [
      { name: "Difficulty achieving an erection", desc: "Trouble getting or maintaining an erection." },
      { name: "Reduced firmness", desc: "An erection that is not firm enough." },
      { name: "Frequent issues", desc: "Problems occurring regularly." },
    ],
    causes: [
      "Cardiovascular and metabolic conditions",
      "Hormonal changes",
      "Psychological factors such as stress and anxiety",
      "Medications",
      "Lifestyle factors",
    ],
    riskFactors: [
      "Diabetes and heart disease",
      "Smoking and excess alcohol",
      "Obesity",
      "Stress and anxiety",
      "Certain medications",
    ],
    whenToSee: [
      "Concerns are persistent or affecting relationships",
      "Symptoms affect confidence or quality of life",
      "You want a professional, confidential opinion",
    ],
    diagnosisSteps: [
      { title: "Private Consultation", desc: "Your concern is discussed confidentially and respectfully." },
      { title: "Health & Lifestyle Review", desc: "Medical and lifestyle factors are considered." },
      { title: "Assessment When Needed", desc: "Simple tests may identify contributing causes." },
      { title: "Discussion of Options", desc: "Treatment and lifestyle options are explained." },
    ],
    treatment: {
      medical: [
        "Management of contributing medical conditions",
        "Appropriate treatment where indicated",
      ],
      procedures: [],
      lifestyle: [
        "Regular exercise",
        "Quitting smoking and limiting alcohol",
        "Managing stress and sleep",
      ],
      followUp:
        "Follow-up depends on the cause and response and is always conducted with discretion.",
    },
    myths: [
      { myth: "Erectile dysfunction is purely psychological.", fact: "It often has physical, psychological or lifestyle causes. A full evaluation identifies the contributing factors." },
      { myth: "Nothing can be done for erectile dysfunction.", fact: "Many causes can be identified and treated, or managed with the right support." },
    ],
    management: [
      "Confidential, private consultation",
      "Identification of contributing factors",
      "Appropriate treatment and lifestyle advice",
    ],
    faqs: [
      { q: "Is erectile dysfunction common?", a: "Yes. It is very common and often related to underlying health, lifestyle or psychological factors that can be evaluated and managed." },
      { q: "Can stress cause erectile dysfunction?", a: "Yes, stress and anxiety are common contributing factors and are addressed as part of a complete assessment." },
      { q: "Is the consultation confidential?", a: "Completely. Consultations are private, professional and judgement-free." },
    ],
    related: ["andrology-consultation", "mens-health", "male-sexual-health"],
    relatedBlogs: ["mens-health-check-what-to-expect"],
    seo: {
      title: "Erectile Dysfunction Treatment in Krishnagiri | Andrologist | AV Clinic",
      description:
        "Confidential erectile dysfunction treatment in Krishnagiri — private evaluation and care by an andrologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "premature-ejaculation",
    name: "Premature Ejaculation Treatment",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "Confidential guidance for a common, manageable concern.",
    tagline: "Confidential, Manageable Care.",
    intro:
      "Premature ejaculation is a very common concern and is often manageable. AV Clinic provides a confidential, professional consultation to understand the issue and plan appropriate, realistic care.",
    image: img.svcPrematureEjaculation,
    symptoms: [
      { name: "Early ejaculation", desc: "Ejaculating sooner than desired." },
      { name: "Reduced control", desc: "Difficulty controlling the timing of ejaculation." },
      { name: "Relationship strain", desc: "Concern affecting confidence or relationships." },
    ],
    causes: [
      "Psychological factors such as anxiety",
      "Sensitivity and habit factors",
      "Hormonal or medical factors",
      "Lifestyle factors",
    ],
    riskFactors: [
      "Stress and anxiety",
      "Certain medical conditions",
      "Relationship factors",
    ],
    whenToSee: [
      "The concern is persistent or affecting relationships",
      "It is causing distress",
      "You want professional, confidential guidance",
    ],
    diagnosisSteps: [
      { title: "Private Consultation", desc: "Your concern is discussed confidentially." },
      { title: "History & Factors", desc: "Psychological and physical factors are considered." },
      { title: "Assessment", desc: "Simple evaluation where needed." },
      { title: "Discussion of Options", desc: "Practical treatment and behavioural options are explained." },
    ],
    treatment: {
      medical: [
        "Appropriate treatment where indicated",
        "Guidance on behavioural techniques",
      ],
      procedures: [],
      lifestyle: [
        "Managing stress and anxiety",
        "Open communication with a partner",
      ],
      followUp:
        "Follow-up is individualised and confidential, and is adjusted based on response.",
    },
    myths: [
      { myth: "Premature ejaculation is uncommon.", fact: "It is one of the most common men's health concerns and is generally manageable." },
      { myth: "Nothing can be done about it.", fact: "Behavioural techniques, treatment and counselling can be very effective for many men." },
    ],
    management: [
      "Confidential consultation",
      "Identification of contributing factors",
      "Behavioural, treatment and lifestyle options",
    ],
    faqs: [
      { q: "Is premature ejaculation common?", a: "Yes, it is one of the most common men's health concerns, and it is generally manageable with the right guidance." },
      { q: "Can anxiety contribute?", a: "Yes, anxiety is a common contributing factor. It is addressed as part of a complete assessment." },
      { q: "Is the consultation confidential?", a: "Yes. Consultations are private, professional and judgement-free." },
    ],
    related: ["andrology-consultation", "male-sexual-health", "erectile-dysfunction"],
    relatedBlogs: ["mens-health-check-what-to-expect"],
    seo: {
      title: "Premature Ejaculation Treatment in Krishnagiri | Andrologist | AV Clinic",
      description:
        "Confidential premature ejaculation treatment in Krishnagiri — private, professional care for a common and manageable concern at AV Clinic.",
    },
  },
  {
    slug: "sperm-analysis",
    name: "Sperm Analysis & Fertility Evaluation",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "Clear evaluation of sperm health and fertility.",
    tagline: "Understanding Sperm Health, Clearly.",
    intro:
      "Sperm analysis is a key step in evaluating male fertility. AV Clinic provides clear, confidential assessment of sperm health and fertility, helping you understand the findings and the next steps.",
    image: img.svcSpermAnalysis,
    symptoms: [
      { name: "Difficulty conceiving", desc: "A couple struggling to conceive." },
      { name: "Known fertility concerns", desc: "Concerns about sperm health." },
      { name: "Need for answers", desc: "A wish to understand fertility potential." },
    ],
    causes: [
      "Low sperm count or motility issues",
      "Sperm shape (morphology) concerns",
      "Hormonal factors",
      "Varicocele or other conditions",
      "Lifestyle factors",
    ],
    whenToSee: [
      "A couple has been trying to conceive without success",
      "A previous semen analysis was abnormal",
      "You want fertility assessment",
    ],
    diagnosisSteps: [
      { title: "Private Consultation", desc: "Your history and concerns are discussed quietly." },
      { title: "Semen Analysis", desc: "A simple test assesses sperm count, movement and shape." },
      { title: "Further Assessment", desc: "Hormones or other tests may be used if needed." },
      { title: "Clear Explanation", desc: "Findings and options are explained with respect." },
    ],
    treatment: {
      medical: [
        "Treatment of reversible causes where possible",
        "Guidance on optimising fertility",
        "Referral for coordinated care where appropriate",
      ],
      procedures: [],
      lifestyle: [
        "Quitting smoking and limiting alcohol",
        "Maintaining a healthy weight",
        "Reducing stress",
      ],
      followUp:
        "Fertility assessment often involves coordinated care. Follow-up is individualised and respectful.",
    },
    myths: [
      { myth: "A single abnormal semen analysis is final.", fact: "Sperm counts can vary. Often more than one sample and a clinical review are used to build an accurate picture." },
      { myth: "Semen analysis is painful.", fact: "It is a simple, non-invasive sample collection, usually done privately." },
    ],
    management: [
      "Confidential sperm analysis and assessment",
      "Explanation of findings",
      "Guidance on next steps and coordinated care",
    ],
    faqs: [
      { q: "How is a sperm analysis done?", a: "A semen sample is collected privately and analysed for count, movement and shape. It is a simple, non-invasive test." },
      { q: "Do I need to prepare?", a: "You may be asked to avoid ejaculation for a few days beforehand. Your specialist will explain any preparation." },
      { q: "What if the result is abnormal?", a: "An abnormal result is not necessarily a final answer. Often it is repeated and reviewed alongside clinical assessment." },
    ],
    related: ["male-infertility", "andrology-consultation", "varicocele"],
    relatedBlogs: ["male-infertility-questions"],
    seo: {
      title: "Sperm Analysis & Fertility Evaluation in Krishnagiri | AV Clinic",
      description:
        "Sperm analysis and fertility evaluation in Krishnagiri — clear, confidential assessment of sperm health by an andrologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "varicocele",
    name: "Varicocele Management",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "Evaluation and care for enlarged veins in the scrotum.",
    tagline: "Understanding Varicocele, Clearly.",
    intro:
      "A varicocele is a swelling of the veins in the scrotum that can affect comfort and, in some cases, fertility. AV Clinic provides confidential evaluation and guidance on management, including when treatment may be helpful.",
    image: img.svcVaricocele,
    symptoms: [
      { name: "Scrotal swelling", desc: "A feeling of heaviness or a lump in the scrotum." },
      { name: "Dull discomfort", desc: "A vague ache that may worsen with standing." },
      { name: "Fertility concerns", desc: "A varicocele found during fertility evaluation." },
    ],
    causes: [
      "Enlarged veins (varicocele)",
      "Abnormal blood flow in the scrotal veins",
      "Genetic predisposition",
    ],
    riskFactors: [
      "Family history of varicocele",
      "Age (more common in young men)",
      "Fertility concerns",
    ],
    whenToSee: [
      "There is a painless or painful swelling in the scrotum",
      "A varicocele was found during a fertility assessment",
      "You have discomfort or concerns",
    ],
    diagnosisSteps: [
      { title: "Private Examination", desc: "The scrotum is examined carefully." },
      { title: "Discussion", desc: "Symptoms and fertility goals are discussed." },
      { title: "Imaging When Needed", desc: "An ultrasound may assess the varicocele." },
      { title: "Plan", desc: "Whether treatment is helpful is decided together." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Treatment for varicocele where it is appropriate",
      ],
      lifestyle: [
        "Avoiding prolonged standing",
        "Supportive undergarments where helpful",
      ],
      followUp:
        "Many varicoceles need no treatment. When treatment is appropriate, follow-up is planned and individualised.",
    },
    myths: [
      { myth: "A varicocele always needs surgery.", fact: "Many varicoceles cause no symptoms and need only monitoring. Treatment is considered when it is appropriate." },
      { myth: "A varicocele always causes infertility.", fact: "A varicocele can contribute to fertility problems in some men, but not always. Evaluation helps clarify the situation." },
    ],
    management: [
      "Confidential evaluation",
      "Assessment of whether treatment is needed",
      "Guidance and follow-up",
    ],
    faqs: [
      { q: "Does a varicocele always need treatment?", a: "No. Many varicoceles need no treatment and are just monitored. Treatment is considered when it is clinically appropriate." },
      { q: "Can a varicocele affect fertility?", a: "In some men, a varicocele can contribute to reduced fertility. Evaluation helps clarify whether it is relevant." },
      { q: "Is treatment for varicocele invasive?", a: "Treatment is usually a minimally invasive procedure. Your specialist will explain the options and what to expect." },
    ],
    related: ["male-infertility", "andrology-consultation", "male-reproductive-health"],
    relatedBlogs: ["male-infertility-questions"],
    seo: {
      title: "Varicocele Management in Krishnagiri | Andrologist | AV Clinic",
      description:
        "Varicocele management in Krishnagiri — confidential evaluation and care for enlarged scrotal veins by an andrologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "male-hormonal-health",
    name: "Male Hormonal & Reproductive Health",
    category: "Andrology",
    categorySlug: "andrology",
    blurb: "Assessment of male hormones and reproductive health.",
    tagline: "Understanding Your Hormones, Confidently.",
    intro:
      "Hormones play a key role in male energy, libido, mood and reproductive health. AV Clinic provides confidential assessment of male hormonal and reproductive health, helping you understand your hormone levels and plan appropriate care.",
    image: img.svcMaleHormonal,
    symptoms: [
      { name: "Low energy", desc: "Persistent tiredness or low motivation." },
      { name: "Reduced libido", desc: "Lower than usual interest in sex." },
      { name: "Mood changes", desc: "Irritability or low mood." },
      { name: "Fertility concerns", desc: "Concerns about hormonal or reproductive health." },
    ],
    causes: [
      "Hormonal imbalance",
      "Age-related change",
      "Lifestyle, stress and sleep",
      "Medical conditions",
    ],
    whenToSee: [
      "You have persistent energy or libido concerns",
      "You want to understand your hormone levels",
      "You have reproductive health questions",
    ],
    diagnosisSteps: [
      { title: "Private Consultation", desc: "Your concerns and history are discussed." },
      { title: "Hormone Assessment", desc: "Simple blood tests may measure relevant hormones." },
      { title: "Interpretation", desc: "Levels are interpreted in the context of your symptoms." },
      { title: "Guidance", desc: "Lifestyle and, where appropriate, treatment options are discussed." },
    ],
    treatment: {
      medical: [
        "Treatment of identified hormonal concerns where appropriate",
        "Guidance on lifestyle and health",
      ],
      procedures: [],
      lifestyle: [
        "Regular exercise and sleep",
        "Balanced nutrition",
        "Managing stress",
      ],
      followUp:
        "Follow-up depends on findings and any treatment. A clear plan is agreed with you.",
    },
    myths: [
      { myth: "Low testosterone is always a problem.", fact: "Hormone levels vary. What matters is whether levels are causing symptoms in your situation, which a specialist can assess." },
      { myth: "Hormone treatment is only for older men.", fact: "Hormonal health is relevant at all ages. Assessment is tailored to your symptoms and needs." },
    ],
    management: [
      "Confidential hormonal assessment",
      "Interpretation of hormone levels",
      "Lifestyle and treatment guidance",
    ],
    faqs: [
      { q: "What does male hormonal assessment involve?", a: "A private consultation, a review of your symptoms and, where appropriate, simple blood tests to measure relevant hormones." },
      { q: "Can lifestyle affect hormones?", a: "Yes. Sleep, exercise, stress and diet can all influence hormone levels and how you feel." },
      { q: "Is the consultation confidential?", a: "Yes. Discussions and results are handled with strict confidentiality." },
    ],
    related: ["andrology-consultation", "male-reproductive-health", "mens-health"],
    relatedBlogs: ["mens-health-check-what-to-expect"],
    seo: {
      title: "Male Hormonal Health in Krishnagiri | Andrologist | AV Clinic",
      description:
        "Confidential male hormonal and reproductive health assessment in Krishnagiri by an andrologist at AV Clinic. Book a consultation.",
    },
  },
];